/**
 * The Gift Guide Hub's two server-owned Firestore documents (THE-582, design in
 * THE-577 plan §3 / §4.1).
 *
 * Both follow the publishing model `theaWebPublishedGuideIndex/current` already
 * uses: ONE singleton `current` document per collection, admin-SDK-written,
 * anonymous-readable, parsed strictly on the client. Nothing here is novel by
 * design — the hub is a layout over data we publish the same way we already
 * publish the guide index.
 *
 *   theaWebHubTrending/current   -> ordered product IDs for the "Trending now" row
 *   theaWebHubOccasions/current  -> occasion label -> guide slugs, in tile order
 *
 * WHY THESE ARE DOCUMENTS AND NOT CODE / DERIVED FIELDS:
 *
 *  - Trending is CURATED TODAY AND COMPUTED LATER. We have no impression/click/
 *    save rollup to rank from, because the hub is the surface that will generate
 *    it. So the board hand-orders `productIds` now, and a nightly rollup can
 *    later overwrite the SAME field with a computed order. That swap must be a
 *    no-op on the client, which is why the reader below takes no position on
 *    where the order came from — it reads an ordered list of product IDs and
 *    stops. There is deliberately no `source`/`curatedAt` field to branch on.
 *
 *  - The occasion -> guide mapping IS the occasion taxonomy. We deliberately did
 *    NOT add a `category` field to the guide index: one occasion needs to point
 *    at several guides, the board needs to reorder and add occasions without a
 *    deploy, and — the real reason — a tile that points at nothing has to be
 *    structurally impossible rather than something we remember to check. See
 *    `resolveHubOccasionTiles`, which is where that guarantee is actually made.
 */
import {
  visiblePublishedGuides,
  type PublishedGuideListing,
} from '../guide/publishedGuides';
import { topicHubPath } from './topicHub';

export const HUB_TRENDING_COLLECTION = 'theaWebHubTrending';
export const HUB_OCCASIONS_COLLECTION = 'theaWebHubOccasions';
export const HUB_FINDS_COLLECTION = 'theaWebHubFinds';
/** All three docs are singletons, same id as the guide index. */
export const HUB_DOC_ID = 'current';

/**
 * Upper bound on the trending row. Every ID costs one product read downstream,
 * and no carousel renders more than a couple of screens' worth — so a writer
 * (today a human, later a rollup) that dumps the whole catalog degrades the hub
 * into a fan-out instead of breaking it loudly. Over-long lists are TRUNCATED,
 * not rejected: dropping tail items the board can't see is strictly better than
 * blanking a row that is otherwise fine.
 */
export const HUB_TRENDING_MAX_PRODUCTS = 24;

/** Same bound, same reasoning, for the Finds band. */
export const HUB_FINDS_MAX_PICKS = 30;

/** Item-price ceiling for the prepared Friday snapshot, inclusive of $25.
 * The legacy ID-only document has no prices; its publisher and adapter use
 * this same cap. Prepared cards retain their snapshot prices until republished.
 */
export const HUB_FINDS_PRICE_MAX = 25;

/**
 * The Finds band's authoring floor: fewer than ten picks is a thin band.
 *
 * ADVISORY HERE, ENFORCED IN THE WRITER. The parser does not reject a short
 * list, for the reason the whole module degrades rather than fails: eight
 * picks render eight cards, and blanking a band that has eight good items
 * because it wanted ten would be the worst of the three outcomes. The
 * publishing script treats it as an error, which is where a human can still
 * do something about it.
 */
export const HUB_FINDS_MIN_PICKS = 10;

/**
 * One curated item in a band, plus WHERE IT CAME FROM (THE-817).
 *
 * `guideSlug` is the whole reason this type exists. Both curated bands — "Our
 * picks this week" and "Finds under $25" — render a chip naming the guide the
 * item was picked out of, and nothing in the catalogue can answer that: a
 * product belongs to many guides (`guide_ids` is multi-valued), so "the"
 * originating guide is an EDITORIAL fact about this pick, not a property of
 * the product. It has to be authored alongside the id or it does not exist.
 *
 * Optional on `parseHubTrending` and required on `parseHubFinds`, and the
 * asymmetry is back-compat rather than design: the trending doc has been live
 * since THE-582 holding a bare `productIds` list, and a reader that demanded
 * attribution would blank the row it has been rendering all along. Finds is
 * new, so it can require the field from its first write — and because it is
 * new, no deployed bundle reads it at all, so it needs none of the mirroring
 * `parseHubTrending` carries for the field it inherited.
 */
export interface HubPick {
  productId: string;
  /** Published guide slug. Resolve against the index before rendering a chip. */
  guideSlug?: string;
}

/** `theaWebHubFinds/current` — the "Finds under $25" band. */
export interface HubFinds {
  /** `YYYY-MM-DD`, the Monday of the week these picks are for. */
  weekOf: string;
  picks: HubPick[];
}

export interface HubOccasion {
  /** Approved collection banner copy. Missing copy uses the existing fallback. */
  description?: string;
  /** Stable key, slug-shaped. Survives a label rename; safe as a React key. */
  key: string;
  label: string;
  emoji?: string;
  /** Published guide slugs, in the order the board wants the guides shown. */
  guideSlugs: string[];
}

/** One rendered occasion tile: an occasion plus its SURVIVING guides. */
export interface HubOccasionTile {
  key: string;
  label: string;
  emoji?: string;
  /** Resolved, in-season listings in mapping order. Never empty. */
  guides: PublishedGuideListing[];
  /**
   * Where the tile navigates: the occasion's own TOPIC PAGE (THE-587).
   *
   * Was the first surviving guide (THE-583), which made the tile a shortcut to
   * one of the guides it stood for — so "Mother's Day" landed on whichever
   * guide happened to be listed first, and the other four were unreachable
   * from the tile. `/gift-guides/{key}` is the stable URL for the whole
   * occasion, which is what the tile has always meant.
   */
  href: string;
}

const record = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Invalid hub document');
  }
  return value as Record<string, unknown>;
};
const string = (value: unknown): string => {
  if (typeof value !== 'string' || !value.trim()) throw new Error('Invalid hub text');
  return value;
};
const array = (value: unknown): unknown[] => {
  if (!Array.isArray(value)) throw new Error('Invalid hub list');
  return value;
};
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const slug = (value: unknown): string => {
  const result = string(value);
  if (!slugPattern.test(result)) throw new Error('Invalid hub slug');
  return result;
};

const productId = (value: unknown): string => {
  const result = string(value);
  if (result.includes('/')) {
    throw new Error('Product ID looks like a path, not an ID');
  }
  return result;
};

/**
 * `[{productId, guideSlug}]` -> picks, with the shared rules applied once.
 *
 * `requireGuideSlug` is the only thing that differs between the two bands; see
 * `HubPick` for why one requires attribution and the other cannot.
 */
function parsePicks(raw: unknown[], label: string, requireGuideSlug: boolean): HubPick[] {
  const picks = raw.map((entry) => {
    const data = record(entry);
    const guideSlug = data.guideSlug === undefined && !requireGuideSlug
      ? undefined
      : slug(data.guideSlug);
    return { productId: productId(data.productId), ...(guideSlug ? { guideSlug } : {}) };
  });
  if (new Set(picks.map((pick) => pick.productId)).size !== picks.length) {
    throw new Error(`Duplicate ${label} product ID`);
  }
  return picks;
}

/**
 * Truncate an over-long band, loudly, rather than rejecting it.
 *
 * The original reasoning (THE-582) applies unchanged to both bands: a writer
 * that dumps the whole catalogue should degrade the band into a short one, not
 * blank a band that is otherwise fine.
 */
function capped(picks: HubPick[], max: number, label: string): HubPick[] {
  if (picks.length <= max) return picks;
  // eslint-disable-next-line no-console
  console.warn(
    `[gift guide hub] ${label} doc holds ${picks.length} picks; rendering the first ${max}`,
  );
  return picks.slice(0, max);
}

/**
 * `theaWebHubTrending/current` -> the ordered picks for "Our picks this week".
 *
 * TWO ACCEPTED SHAPES, and the older one is not deprecated so much as
 * unattributed:
 *
 *   {productIds: ["a", "b"]}                       THE-582, no guide chips
 *   {picks: [{productId: "a", guideSlug: "..."}]}  THE-817, with chips
 *
 * Reading both is not politeness toward old data, it is half of what this
 * change needs in order to ship without a flag-day. A reader that understood
 * only the new shape would blank the row for everyone between the deploy and
 * the write, and a bundle rolled back after the write would blank it again.
 * Each form carries exactly what it carries: a legacy row renders with no
 * chips, because there is genuinely no attribution in it to render.
 *
 * THE OTHER HALF IS THE MIRROR, and it is here because reading both was NOT
 * enough (THE-828). A dual-READ only covers new-bundle-meets-old-doc. The
 * reverse — old bundle meets new doc — is the one that actually happened: the
 * `picks` write went out on 2026-10-05 while this reader was still an open PR,
 * so every deployed bundle hit `array(undefined)`, threw, and blanked the row
 * in production. The doc is board-written with no deploy, which makes the write
 * the EASY half to land and the read the slow one; "either order" was never
 * true, and only a document carrying BOTH fields is safe in both directions.
 *
 * So `productIds` alongside `picks` is a MIRROR, not a second opinion: it must
 * be exactly the picks' product IDs, in the same order. That keeps the original
 * objection to a precedence rule intact — two different orderings in one
 * document still has no correct answer, and is still refused — while letting
 * the writer publish one document that an old bundle and a new one both render
 * identically, modulo chips. A half-finished edit (new `picks` added, old
 * `productIds` left behind) disagrees, so it throws, which is what the old
 * both-is-an-error rule was protecting.
 *
 * Throws on a malformed SHAPE; callers (the hook) turn that into an empty row.
 * A product ID that doesn't resolve to a live product is NOT a parse error and
 * is not checked here — product hydration already drops unknown/unavailable
 * IDs, exactly as it does for a guide's `productIds`. Duplicates are rejected
 * because a repeated tile in one row is always an authoring mistake.
 */
export function parseHubTrending(value: unknown): HubPick[] {
  const data = record(value);
  if (data.picks === undefined) {
    const ids = array(data.productIds).map((id) => productId(id));
    if (new Set(ids).size !== ids.length) {
      throw new Error('Duplicate hub trending product ID');
    }
    return capped(ids.map((id) => ({ productId: id })), HUB_TRENDING_MAX_PRODUCTS, 'trending');
  }
  const picks = parsePicks(array(data.picks), 'hub trending', false);
  if (data.productIds !== undefined) {
    // The mirror, compared BEFORE `capped` — both fields are full lists, and a
    // writer that truncated one and not the other is exactly the disagreement
    // worth catching.
    const mirror = array(data.productIds).map((id) => productId(id));
    const expected = picks.map((pick) => pick.productId);
    if (
      mirror.length !== expected.length
      || mirror.some((id, index) => id !== expected[index])
    ) {
      throw new Error('Hub trending productIds is not a mirror of picks');
    }
  }
  return capped(picks, HUB_TRENDING_MAX_PRODUCTS, 'trending');
}

/**
 * `theaWebHubFinds/current` -> the "Finds under $25" band (THE-817).
 *
 * A SEPARATE DOCUMENT FROM TRENDING, not a filtered view of it, because the
 * two bands answer different questions and are curated against different
 * constraints: trending is "what is moving", finds is "ten good things under
 * $25". Deriving one from the other would mean the cheap band silently
 * shrinks whenever the trending list happens to skew expensive — the band's
 * length would be an accident of another band's curation.
 *
 * `weekOf` exists so the band can say *when* it was last refreshed and so a
 * stale week is visible rather than inferred. It is NOT a gate: a doc whose
 * week has passed still renders, because a slightly old band of good cheap
 * gifts beats an empty one, and the weekly refresh is a standing authoring
 * commitment rather than a guarantee the code can enforce.
 *
 * `guideSlug` IS required on every pick here — see `HubPick`. Finds has no
 * legacy shape to stay compatible with, and the PRD puts a chip under every
 * item in this band, so a pick that cannot render one is an authoring bug
 * worth catching on the first write instead of a card with a hole in it.
 */
export function parseHubFinds(value: unknown): HubFinds {
  const data = record(value);
  const weekOf = string(data.weekOf);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(weekOf)) {
    throw new Error('Invalid hub finds weekOf');
  }
  return {
    weekOf,
    picks: capped(
      parsePicks(array(data.picks), 'hub finds', true),
      HUB_FINDS_MAX_PICKS,
      'finds',
    ),
  };
}

/**
 * `theaWebHubOccasions/current` -> the occasions, in tile order.
 *
 * The occasions are an ARRAY, not the `{ key: { displayOrder } }` map the guide
 * index uses. That divergence is deliberate: guides are addressed individually
 * by slug (hence a map) and need a sparse, decimal-insertable order, while
 * occasions are only ever read as one ordered list by one surface. An array IS
 * the order, so a hand-edited doc cannot express an ordering bug, and the board
 * reorders tiles by moving a JSON block instead of renumbering.
 */
export function parseHubOccasions(value: unknown): HubOccasion[] {
  const occasions = array(record(value).occasions).map((raw) => {
    const data = record(raw);
    const emoji = data.emoji === undefined ? undefined : string(data.emoji);
    const description = typeof data.description === 'string' ? data.description.trim() : undefined;
    const guideSlugs = array(data.guideSlugs).map(slug);
    if (!guideSlugs.length) throw new Error('Occasion maps to no guides');
    if (new Set(guideSlugs).size !== guideSlugs.length) {
      throw new Error('Duplicate guide slug in occasion');
    }
    return {
      key: slug(data.key),
      label: string(data.label),
      ...(emoji ? { emoji } : {}),
      guideSlugs,
      ...(description ? { description } : {}),
    };
  });
  if (new Set(occasions.map((o) => o.key)).size !== occasions.length) {
    throw new Error('Duplicate occasion key');
  }
  return occasions;
}

/**
 * Resolve occasions into renderable tiles against the published guide index.
 *
 * THIS IS WHERE THE "NO TILE POINTS AT NOTHING" GUARANTEE LIVES, and it is the
 * reason the mapping is allowed to be hand-authored. Two things get dropped:
 *
 *  1. A slug with no listing in the index — the guide was never published, was
 *    deleted, or the board typo'd it. A retired guide must not strand a tile.
 *  2. A listing that is OUT OF ITS `season` WINDOW. `/gift-guides` already hides
 *    off-season guides, so a tile linking to one would be a link into a guide
 *    the rest of the site is currently pretending doesn't exist. Reusing
 *    `visiblePublishedGuides` rather than re-deriving the window keeps one
 *    definition of "in season" for the whole app.
 *
 * An occasion whose every guide was dropped is itself dropped. So FEWER TILES
 * THAN MAPPING ENTRIES IS CORRECT BEHAVIOUR, not a bug — a "Thanksgiving" row
 * mapped only to guides that open on 10-15 renders nothing until 10-15, which is
 * the same seasonal rule the browse tab follows.
 */
export function resolveHubOccasionTiles(
  occasions: HubOccasion[],
  listings: PublishedGuideListing[],
  date: Date,
): HubOccasionTile[] {
  const visible = new Map(
    visiblePublishedGuides(listings, date).map((listing) => [listing.key, listing]),
  );
  const tiles: HubOccasionTile[] = [];
  occasions.forEach(({ key, label, emoji, guideSlugs }) => {
    const guides = guideSlugs
      .map((guideSlug) => visible.get(guideSlug))
      .filter((listing): listing is PublishedGuideListing => listing !== undefined);
    if (!guides.length) return;
    // `guides` is still resolved and still required non-empty: a topic page
    // for an occasion whose every guide is out of season would be a tile that
    // leads somewhere real but empty, and "a tile that points at nothing is
    // structurally impossible" is this function's whole job.
    tiles.push({ key, label, ...(emoji ? { emoji } : {}), guides, href: topicHubPath(key) });
  });
  return tiles;
}
